import { useCallback, useEffect, useRef } from "react";
import "hammerjs";
import { parseDateTimeLocal, toDateTimeLocalValue } from "../utils/datetime";
import type { ChartPoint, BpsLogPoint } from "../utils/types";

const MIN_PERIOD_RANGE_MS = 1000;
const MIN_REASONABLE_INTERACTION_MS = Date.parse("2000-01-01T00:00:00Z");
const MIN_ALLOWED_PERIOD_MS = Date.parse("2020-01-01T00:00:00Z");

// 期間指定のstart/end(ms)を許容範囲内（未来禁止/2020以降/最小幅）にクランプする。
const clampPeriodRange = (minMs: number, maxMs: number) => {
  const min = Math.min(minMs, maxMs);
  const max = Math.max(minMs, maxMs);
  const spanMs = Math.max(MIN_PERIOD_RANGE_MS, max - min);

  // 上限（未来）を禁止する。
  const nowMs = Date.now();
  let clampedMin = min;
  let clampedMax = max;
  if (clampedMax > nowMs) {
    clampedMax = nowMs;
    clampedMin = clampedMax - spanMs;
  }

  // 下限（2020-01-01未満）を禁止する。
  if (clampedMin < MIN_ALLOWED_PERIOD_MS) {
    clampedMin = MIN_ALLOWED_PERIOD_MS;
    clampedMax = clampedMin + spanMs;
    if (clampedMax > nowMs) {
      clampedMax = nowMs;
      clampedMin = Math.max(MIN_ALLOWED_PERIOD_MS, clampedMax - spanMs);
    }
  }

  if (clampedMax - clampedMin < MIN_PERIOD_RANGE_MS) {
    clampedMax = clampedMin + MIN_PERIOD_RANGE_MS;
  }

  return { min: clampedMin, max: clampedMax };
};

const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v));

// chartjs-plugin-zoom@2.2.0 は wheel.speed を number として期待するため、spanから適応的に速度を算出する。
const getAdaptiveWheelZoomSpeed = (spanMs: number): number => {
  const safeSpanMs = Math.max(MIN_PERIOD_RANGE_MS, Math.abs(spanMs));

  // spanMsの対数スケールで「1分≈0.05、1日≈0.25」になるように補間する。
  const a1Ms = 60_000;
  const a2Ms = 86_400_000;
  const s1 = 0.05;
  const s2 = 0.25;

  const log1 = Math.log10(a1Ms);
  const log2 = Math.log10(a2Ms);
  const m = (s2 - s1) / (log2 - log1);
  const b = s1 - m * log1;

  const raw = m * Math.log10(safeSpanMs) + b;
  return clamp(raw, 0.02, 0.35);
};

type RangeRef = { start: string; end: string };

type Params = {
  isRealtime: boolean;
  isRealtimeRef: React.MutableRefObject<boolean>;
  realtimeWindowMinutes: number;
  selectedTimestampForHeader: string | null;

  leftStackRef: React.MutableRefObject<HTMLDivElement | null>;

  chartRefs: React.MutableRefObject<Array<any>>;
  chartPointsRef: React.MutableRefObject<ChartPoint[]>;
  bpslogDataForDisplayRef: React.MutableRefObject<BpsLogPoint[]>;

  appliedRangeRef: React.MutableRefObject<RangeRef>;
  appliedStartInput: string;
  appliedEndInput: string;
  startInput: string;
  endInput: string;

  setIsRealtime: (next: boolean) => void;
  setStartInput: (v: string) => void;
  setEndInput: (v: string) => void;
  setAppliedStartInput: (v: string) => void;
  setAppliedEndInput: (v: string) => void;
};

export const useBpsLogChartZoom = (params: Params) => {
  // チャートのホイール/パン/ピンチ操作を監視し、期間指定のstart/endやリアルタイム表示へ反映する。
  const {
    isRealtime,
    isRealtimeRef,
    realtimeWindowMinutes,
    selectedTimestampForHeader,
    leftStackRef,
    chartRefs,
    chartPointsRef,
    bpslogDataForDisplayRef,
    appliedRangeRef,
    appliedStartInput,
    appliedEndInput,
    startInput,
    endInput,
    setIsRealtime,
    setStartInput,
    setEndInput,
    setAppliedStartInput,
    setAppliedEndInput,
  } = params;

  const realtimeWindowMinutesRef = useRef(realtimeWindowMinutes);
  const selectedTimestampForHeaderRef = useRef(selectedTimestampForHeader);
  const startInputRef = useRef(startInput);
  const endInputRef = useRef(endInput);

  useEffect(() => {
    realtimeWindowMinutesRef.current = realtimeWindowMinutes;
  }, [realtimeWindowMinutes]);

  useEffect(() => {
    selectedTimestampForHeaderRef.current = selectedTimestampForHeader;
  }, [selectedTimestampForHeader]);

  useEffect(() => {
    startInputRef.current = startInput;
  }, [startInput]);

  useEffect(() => {
    endInputRef.current = endInput;
  }, [endInput]);

  const ignoreZoomEventRef = useRef(false);
  const zoomFetchTimeoutRef = useRef<number | null>(null);
  const panFetchTimeoutRef = useRef<number | null>(null);

  const zoomFetchRafRef = useRef<number | null>(null);
  const zoomPendingRangeRef = useRef<{ startMs: number; endMs: number } | null>(
    null,
  );

  const panFetchRafRef = useRef<number | null>(null);
  const panPendingRangeRef = useRef<{ startMs: number; endMs: number } | null>(
    null,
  );

  const wheelZoomOutApplyRafRef = useRef<number | null>(null);
  const wheelZoomOutThrottleTimeoutRef = useRef<number | null>(null);
  const wheelZoomOutLastApplyMsRef = useRef<number>(0);
  const wheelZoomOutStateRef = useRef<{
    steps: number;
    baseSpanMs: number;
    centerMs: number;
  }>({
    steps: 0,
    baseSpanMs: 0,
    centerMs: Date.now(),
  });

  const panRafRef = useRef<number | null>(null);
  const panLastFrameMsRef = useRef<number>(0);
  const panVelocityMsPerFrameRef = useRef<number>(0);
  const panXRangeRef = useRef<{ min: number; max: number } | null>(null);
  const panLastApplyMsRef = useRef<number>(0);

  useEffect(() => {
    // 外部要因で期間が変わった場合は、進行中のパン慣性アニメーションを打ち切る。
    if (panRafRef.current !== null) {
      window.cancelAnimationFrame(panRafRef.current);
      panRafRef.current = null;
    }
    panLastFrameMsRef.current = 0;
    panLastApplyMsRef.current = 0;
    panVelocityMsPerFrameRef.current = 0;
    panXRangeRef.current = null;
  }, [isRealtime, appliedStartInput, appliedEndInput]);

  useEffect(() => {
    return () => {
      if (zoomFetchTimeoutRef.current !== null) {
        window.clearTimeout(zoomFetchTimeoutRef.current);
        zoomFetchTimeoutRef.current = null;
      }
      if (panFetchTimeoutRef.current !== null) {
        window.clearTimeout(panFetchTimeoutRef.current);
        panFetchTimeoutRef.current = null;
      }
      if (zoomFetchRafRef.current !== null) {
        window.cancelAnimationFrame(zoomFetchRafRef.current);
        zoomFetchRafRef.current = null;
      }
      if (panFetchRafRef.current !== null) {
        window.cancelAnimationFrame(panFetchRafRef.current);
        panFetchRafRef.current = null;
      }
      if (wheelZoomOutApplyRafRef.current !== null) {
        window.cancelAnimationFrame(wheelZoomOutApplyRafRef.current);
        wheelZoomOutApplyRafRef.current = null;
      }
      if (wheelZoomOutThrottleTimeoutRef.current !== null) {
        window.clearTimeout(wheelZoomOutThrottleTimeoutRef.current);
        wheelZoomOutThrottleTimeoutRef.current = null;
      }
      if (panRafRef.current !== null) {
        window.cancelAnimationFrame(panRafRef.current);
        panRafRef.current = null;
      }
    };
  }, []);

  const applyPeriodRangeFromMs = useCallback(
    (startMs: number, endMs: number) => {
      if (isRealtimeRef.current) return;
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return;

      let minMs = Math.min(startMs, endMs);
      let maxMs = Math.max(startMs, endMs);

      // 期間指定の上限/下限（未来禁止・2020以降）を強制する。
      ({ min: minMs, max: maxMs } = clampPeriodRange(minMs, maxMs));

      // 稀に0付近へ飛ぶ異常値を弾き、操作起因の自動更新でepochへジャンプする事故を防ぐ。
      if (maxMs < MIN_REASONABLE_INTERACTION_MS) return;

      // datetime-local は秒精度のため、ズームしすぎると start/end が同一秒になりやすい。
      // period range が 0 になるとホイールズームアウトが不能になるため、最小幅を保証する。
      if (maxMs - minMs < MIN_PERIOD_RANGE_MS) {
        maxMs = minMs + MIN_PERIOD_RANGE_MS;
      }

      const startAt = new Date(minMs);
      const endAt = new Date(maxMs);
      const nextStart = toDateTimeLocalValue(startAt);
      const nextEnd = toDateTimeLocalValue(endAt);

      const cur = appliedRangeRef.current;
      if (cur.start === nextStart && cur.end === nextEnd) return;

      // 連続ホイール中でも次回計算が直前の適用値を見られるよう、ref を先に同期する。
      appliedRangeRef.current = { start: nextStart, end: nextEnd };
      startInputRef.current = nextStart;
      endInputRef.current = nextEnd;

      setStartInput(nextStart);
      setEndInput(nextEnd);
      setAppliedStartInput(nextStart);
      setAppliedEndInput(nextEnd);
    },
    [
      appliedRangeRef,
      isRealtimeRef,
      setAppliedEndInput,
      setAppliedStartInput,
      setEndInput,
      setStartInput,
    ],
  );

  const scheduleFetchPeriodRangeFromZoom = useCallback(
    (startMs: number, endMs: number) => {
      // 連続入力で中間状態を捨て、最終レンジだけを次フレームで適用する。
      zoomPendingRangeRef.current = { startMs, endMs };
      if (zoomFetchRafRef.current !== null) return;

      zoomFetchRafRef.current = window.requestAnimationFrame(() => {
        zoomFetchRafRef.current = null;
        if (isRealtimeRef.current) return;

        const pending = zoomPendingRangeRef.current;
        zoomPendingRangeRef.current = null;
        if (!pending) return;

        applyPeriodRangeFromMs(pending.startMs, pending.endMs);
      });
    },
    [applyPeriodRangeFromMs, isRealtimeRef],
  );

  const scheduleFetchPeriodRangeFromPan = useCallback(
    (startMs: number, endMs: number) => {
      panPendingRangeRef.current = { startMs, endMs };
      if (panFetchRafRef.current !== null) return;

      panFetchRafRef.current = window.requestAnimationFrame(() => {
        panFetchRafRef.current = null;
        if (isRealtimeRef.current) return;

        const pending = panPendingRangeRef.current;
        panPendingRangeRef.current = null;
        if (!pending) return;

        applyPeriodRangeFromMs(pending.startMs, pending.endMs);
      });
    },
    [applyPeriodRangeFromMs, isRealtimeRef],
  );

  const scheduleFetchPeriodRangeFromWheelZoomOut = useCallback(
    (centerMs: number, baseSpanMs: number, stepsDelta: number) => {
      if (!Number.isFinite(centerMs) || !Number.isFinite(baseSpanMs)) return;
      const safeBaseSpanMs = Math.max(MIN_PERIOD_RANGE_MS, baseSpanMs);

      const safeDelta =
        typeof stepsDelta === "number" && Number.isFinite(stepsDelta)
          ? Math.trunc(stepsDelta)
          : 1;
      const add = Math.max(1, Math.min(10, safeDelta));

      const nextSteps = Math.min(40, wheelZoomOutStateRef.current.steps + add);
      wheelZoomOutStateRef.current.steps = nextSteps;
      wheelZoomOutStateRef.current.baseSpanMs = safeBaseSpanMs;
      wheelZoomOutStateRef.current.centerMs = centerMs;

      const applyNow = () => {
        if (isRealtimeRef.current) return;

        const { steps, baseSpanMs, centerMs } = wheelZoomOutStateRef.current;
        wheelZoomOutStateRef.current.steps = 0;

        const speed = getAdaptiveWheelZoomSpeed(baseSpanMs);
        const factor = clamp(1 + speed * 1.25, 1.05, 1.65);
        const nextSpanMs = Math.max(
          30_000,
          baseSpanMs * Math.pow(factor, Math.max(1, steps)),
        );
        applyPeriodRangeFromMs(
          centerMs - nextSpanMs / 2,
          centerMs + nextSpanMs / 2,
        );
      };

      // 先頭は即時（次フレーム）で反応させ、連続ホイール中は軽く間引いて取得連打を抑える。
      const throttleMs = 80;
      const nowMs =
        typeof performance !== "undefined" &&
        typeof performance.now === "function"
          ? performance.now()
          : Date.now();
      const elapsed = nowMs - wheelZoomOutLastApplyMsRef.current;

      const scheduleApply = () => {
        if (wheelZoomOutApplyRafRef.current !== null) return;
        wheelZoomOutApplyRafRef.current = window.requestAnimationFrame(() => {
          wheelZoomOutApplyRafRef.current = null;
          const nowMs =
            typeof performance !== "undefined" &&
            typeof performance.now === "function"
              ? performance.now()
              : Date.now();
          wheelZoomOutLastApplyMsRef.current = nowMs;
          applyNow();
        });
      };

      if (elapsed >= throttleMs) {
        if (wheelZoomOutThrottleTimeoutRef.current !== null) {
          window.clearTimeout(wheelZoomOutThrottleTimeoutRef.current);
          wheelZoomOutThrottleTimeoutRef.current = null;
        }
        scheduleApply();
        return;
      }

      if (wheelZoomOutThrottleTimeoutRef.current !== null) return;
      const waitMs = Math.max(0, Math.ceil(throttleMs - elapsed));
      wheelZoomOutThrottleTimeoutRef.current = window.setTimeout(() => {
        wheelZoomOutThrottleTimeoutRef.current = null;
        scheduleApply();
      }, waitMs);
    },
    [applyPeriodRangeFromMs, isRealtimeRef],
  );

  const applyXRangeToAllCharts = useCallback(
    (minValue: number, maxValue: number, updateMode?: any) => {
      if (!Number.isFinite(minValue) || !Number.isFinite(maxValue)) return;

      let clampedMin = Math.min(minValue, maxValue);
      let clampedMax = Math.max(minValue, maxValue);

      if (!isRealtimeRef.current) {
        ({ min: clampedMin, max: clampedMax } = clampPeriodRange(
          clampedMin,
          clampedMax,
        ));

        // 稀に0付近へ飛ぶ異常値を弾き、epochへジャンプする事故を防ぐ。
        if (clampedMax < MIN_REASONABLE_INTERACTION_MS) return;
      }

      if (clampedMax - clampedMin < MIN_PERIOD_RANGE_MS) {
        clampedMax = clampedMin + MIN_PERIOD_RANGE_MS;
      }

      ignoreZoomEventRef.current = true;
      try {
        for (const c of chartRefs.current) {
          const chart = (c as any)?.chart ?? (c as any);
          if (!chart) continue;

          const scales = (chart?.options?.scales as any) ?? undefined;
          const xScale = scales?.x;
          if (xScale) {
            xScale.min = clampedMin;
            xScale.max = clampedMax;
          }

          if (typeof chart.update === "function") {
            const mode =
              updateMode ?? (isRealtimeRef.current ? "none" : undefined);
            chart.update(mode);
          }
        }
      } finally {
        window.setTimeout(() => {
          ignoreZoomEventRef.current = false;
        }, 0);
      }
    },
    [chartRefs, isRealtimeRef],
  );

  const getAppliedRangeMsFallback = useCallback(() => {
    const s =
      parseDateTimeLocal(appliedRangeRef.current.start) ??
      parseDateTimeLocal(startInputRef.current);
    const e =
      parseDateTimeLocal(appliedRangeRef.current.end) ??
      parseDateTimeLocal(endInputRef.current);
    if (!s || !e) return null;

    let min = Math.min(s.getTime(), e.getTime());
    let max = Math.max(s.getTime(), e.getTime());
    if (!Number.isFinite(min) || !Number.isFinite(max)) return null;

    // 「未来禁止」ポリシーと整合するように補正する。
    const nowMs = Date.now();
    if (max > nowMs) {
      const spanMs = Math.max(MIN_PERIOD_RANGE_MS, max - min);
      max = nowMs;
      min = max - spanMs;
    }

    if (max - min < MIN_PERIOD_RANGE_MS) max = min + MIN_PERIOD_RANGE_MS;
    return { min, max };
  }, [appliedRangeRef, endInputRef, startInputRef]);

  const getCurrentXRangeFromChart = useCallback(
    (chartIdx: number): { min: number; max: number } | null => {
      try {
        const ref = chartRefs.current[chartIdx] as any;
        const chart = ref?.chart ?? ref;
        const xScale = chart?.scales?.x;
        const minRaw = xScale?.min;
        const maxRaw = xScale?.max;
        if (typeof minRaw !== "number" || typeof maxRaw !== "number")
          return null;
        const min = Math.min(minRaw, maxRaw);
        const max = Math.max(minRaw, maxRaw);
        if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
        if (max - min < MIN_PERIOD_RANGE_MS)
          return { min, max: min + MIN_PERIOD_RANGE_MS };
        return { min, max };
      } catch {
        return null;
      }
    },
    [chartRefs],
  );

  const repairBrokenXRangeIfNeeded = useCallback(
    (chartIdx: number) => {
      if (isRealtimeRef.current) return;

      const cur = getCurrentXRangeFromChart(chartIdx);
      const nowMs = Date.now();
      const looksBroken =
        !cur ||
        !Number.isFinite(cur.min) ||
        !Number.isFinite(cur.max) ||
        cur.max < MIN_REASONABLE_INTERACTION_MS ||
        cur.max > nowMs + 60_000;
      if (!looksBroken) return;

      const applied = getAppliedRangeMsFallback();
      if (!applied) return;
      applyXRangeToAllCharts(applied.min, applied.max, "none");
    },
    [
      applyXRangeToAllCharts,
      getAppliedRangeMsFallback,
      getCurrentXRangeFromChart,
      isRealtimeRef,
    ],
  );

  const stepPanAnimation = useCallback(
    (nowMs: number) => {
      if (panLastFrameMsRef.current === 0) {
        panLastFrameMsRef.current = nowMs;
      }

      const dtMs = Math.max(0, nowMs - panLastFrameMsRef.current);
      panLastFrameMsRef.current = nowMs;

      // 30fps程度に抑える（updateが重くなりやすいので）
      if (nowMs - panLastApplyMsRef.current < 30) {
        panRafRef.current = window.requestAnimationFrame(stepPanAnimation);
        return;
      }
      panLastApplyMsRef.current = nowMs;

      const range = panXRangeRef.current;
      if (!range) {
        panRafRef.current = null;
        panLastFrameMsRef.current = 0;
        panVelocityMsPerFrameRef.current = 0;
        return;
      }

      // dtに応じた減衰（指数）
      const frames = Math.max(0.5, dtMs / 16.67);
      const dampingPerFrame = 0.5;
      const damping = Math.pow(dampingPerFrame, frames);
      panVelocityMsPerFrameRef.current *= damping;

      const moveMs = panVelocityMsPerFrameRef.current * frames;
      range.min += moveMs;
      range.max += moveMs;

      // 未来禁止境界に当たったら内部レンジも揃え、以後未来方向の速度が残らないように止める。
      if (!isRealtimeRef.current) {
        const before = { min: range.min, max: range.max };
        const clamped = clampPeriodRange(range.min, range.max);
        range.min = clamped.min;
        range.max = clamped.max;

        const nowMs = Date.now();
        if (before.max > nowMs && panVelocityMsPerFrameRef.current > 0) {
          panVelocityMsPerFrameRef.current = 0;
        }
        if (
          before.min < MIN_ALLOWED_PERIOD_MS &&
          panVelocityMsPerFrameRef.current < 0
        ) {
          panVelocityMsPerFrameRef.current = 0;
        }
      }

      applyXRangeToAllCharts(range.min, range.max, "none");

      if (Math.abs(panVelocityMsPerFrameRef.current) < 0.5) {
        // パンが落ち着いた後に1回だけ適用/取得する。
        scheduleFetchPeriodRangeFromPan(range.min, range.max);
        panRafRef.current = null;
        panLastFrameMsRef.current = 0;
        panVelocityMsPerFrameRef.current = 0;
        return;
      }

      panRafRef.current = window.requestAnimationFrame(stepPanAnimation);
    },
    [applyXRangeToAllCharts, scheduleFetchPeriodRangeFromPan],
  );

  const addPanImpulse = useCallback(
    (chartIdx: number, impulseMs: number) => {
      if (!Number.isFinite(impulseMs) || impulseMs === 0) return;

      if (!panXRangeRef.current) {
        panXRangeRef.current =
          getCurrentXRangeFromChart(chartIdx) ??
          (() => {
            const s = parseDateTimeLocal(appliedRangeRef.current.start);
            const e = parseDateTimeLocal(appliedRangeRef.current.end);
            if (!s || !e) return null;
            const min = Math.min(s.getTime(), e.getTime());
            const max = Math.max(s.getTime(), e.getTime());
            return { min, max: Math.max(min + MIN_PERIOD_RANGE_MS, max) };
          })();
      }

      // 1回のホイールでの最大移動量をクランプ（暴れ防止）
      const range = panXRangeRef.current;
      const spanMs = range
        ? Math.max(MIN_PERIOD_RANGE_MS, range.max - range.min)
        : 60_000;
      const clamped = Math.max(
        -spanMs * 1.5,
        Math.min(spanMs * 1.5, impulseMs),
      );

      // 速度に加算（慣性）
      panVelocityMsPerFrameRef.current += clamped;

      if (panRafRef.current === null) {
        panLastFrameMsRef.current = 0;
        panLastApplyMsRef.current = 0;
        panRafRef.current = window.requestAnimationFrame(stepPanAnimation);
      }
    },
    [appliedRangeRef, getCurrentXRangeFromChart, stepPanAnimation],
  );

  // 期間指定の「適用期間」とグラフのX軸レンジを一致させる。
  // data点が端まで無い場合でも、軸レンジだけは必ず揃える（線は実データ範囲で止まる）。
  useEffect(() => {
    if (isRealtime) return;
    const s = parseDateTimeLocal(appliedStartInput);
    const e = parseDateTimeLocal(appliedEndInput);
    if (!s || !e) return;
    const startMs = s.getTime();
    const endMs = e.getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return;
    const minValue = Math.min(startMs, endMs);
    const maxValue = Math.max(startMs, endMs);
    applyXRangeToAllCharts(minValue, maxValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRealtime, appliedStartInput, appliedEndInput]);

  const handleZoomOrPanComplete = useCallback(
    (chart: any) => {
      if (ignoreZoomEventRef.current) return;

      const xScale = chart?.scales?.x;
      const minRaw = xScale?.min;
      const maxRaw = xScale?.max;

      if (typeof minRaw !== "number" || typeof maxRaw !== "number") return;
      let visibleStartMs = Math.min(minRaw, maxRaw);
      let visibleEndMs = Math.max(minRaw, maxRaw);
      if (!Number.isFinite(visibleStartMs) || !Number.isFinite(visibleEndMs)) {
        return;
      }

      // plugin由来で未来側へはみ出したレンジは未来禁止に合わせてクランプする。
      const nowMs = Date.now();
      if (visibleEndMs > nowMs) {
        const spanMs = Math.max(
          MIN_PERIOD_RANGE_MS,
          visibleEndMs - visibleStartMs,
        );
        visibleEndMs = nowMs;
        visibleStartMs = visibleEndMs - spanMs;
      }

      // pluginが壊れたレンジ(例:0..1)を返した場合は、適用済みレンジへ復旧する。
      if (visibleEndMs < MIN_REASONABLE_INTERACTION_MS) {
        const applied = getAppliedRangeMsFallback();
        if (applied) applyXRangeToAllCharts(applied.min, applied.max);
        return;
      }
      if (visibleEndMs - visibleStartMs < MIN_PERIOD_RANGE_MS) {
        visibleEndMs = visibleStartMs + MIN_PERIOD_RANGE_MS;
      }
      const visibleSpanMs = visibleEndMs - visibleStartMs;
      if (!Number.isFinite(visibleSpanMs) || visibleSpanMs <= 0) return;

      const appliedStartAt = parseDateTimeLocal(appliedRangeRef.current.start);
      const appliedEndAt = parseDateTimeLocal(appliedRangeRef.current.end);
      const appliedSpanMs =
        appliedStartAt && appliedEndAt
          ? appliedEndAt.getTime() - appliedStartAt.getTime()
          : null;

      // 期間指定: 全チャートで表示範囲を同期
      applyXRangeToAllCharts(visibleStartMs, visibleEndMs);

      // ズームイン（範囲が狭い）: その表示範囲で再取得
      if (typeof appliedSpanMs === "number" && Number.isFinite(appliedSpanMs)) {
        const safeAppliedSpanMs = Math.max(
          MIN_PERIOD_RANGE_MS,
          Math.abs(appliedSpanMs),
        );
        if (visibleSpanMs < safeAppliedSpanMs * 0.98) {
          scheduleFetchPeriodRangeFromZoom(visibleStartMs, visibleEndMs);
          return;
        }
      }
    },
    [
      applyXRangeToAllCharts,
      appliedRangeRef,
      getAppliedRangeMsFallback,
      ignoreZoomEventRef,
      scheduleFetchPeriodRangeFromZoom,
    ],
  );

  const switchToPeriodByZoom = useCallback(
    (zoomFactor: number, centerMs: number) => {
      const safeFactor = Number.isFinite(zoomFactor) ? zoomFactor : 1;
      const baseSpanMs = realtimeWindowMinutesRef.current * 60_000;
      // ズームアウト側の安全機構（上限）は API 側で行う方針のため、最大値はクランプしない。
      const nextSpanMs = Math.max(
        30_000, // 30秒
        baseSpanMs * safeFactor,
      );

      const nowMs = Date.now();
      const safeCenterMsRaw = Number.isFinite(centerMs) ? centerMs : nowMs;
      const safeCenterMs = Math.min(safeCenterMsRaw, nowMs);
      let nextStartMs = safeCenterMs - nextSpanMs / 2;
      let nextEndMs = safeCenterMs + nextSpanMs / 2;

      // リアルタイム末尾付近を中心に期間指定へ切り替えると future 側にはみ出しやすい。
      ({ min: nextStartMs, max: nextEndMs } = clampPeriodRange(
        nextStartMs,
        nextEndMs,
      ));

      const startAt = new Date(nextStartMs);
      const endAt = new Date(nextEndMs);

      const nextStart = toDateTimeLocalValue(startAt);
      const nextEnd = toDateTimeLocalValue(endAt);

      // リアルタイムから期間指定へ切り替わる同一ホイール連打中でも、
      // 次のイベントが新しい表示幅を基準に計算できるよう ref を即時更新する。
      appliedRangeRef.current = { start: nextStart, end: nextEnd };
      startInputRef.current = nextStart;
      endInputRef.current = nextEnd;

      // ハンドラ内で連続発火する可能性があるため、refも先に更新
      isRealtimeRef.current = false;
      setIsRealtime(false);

      setStartInput(nextStart);
      setEndInput(nextEnd);
      setAppliedStartInput(nextStart);
      setAppliedEndInput(nextEnd);
    },
    [
      isRealtimeRef,
      setAppliedEndInput,
      setAppliedStartInput,
      setEndInput,
      setIsRealtime,
      setStartInput,
    ],
  );

  const getCenterMsFromWheel = useCallback(
    (e: WheelEvent, chartIdx: number) => {
      // 可能ならカーソル位置に近い時刻を中心にする。
      try {
        const ref = chartRefs.current[chartIdx] as any;
        const chart = ref?.chart ?? ref;
        const canvas: HTMLCanvasElement | undefined = chart?.canvas;
        const rect = canvas?.getBoundingClientRect();
        const clientX = e?.clientX ?? null;
        if (!rect || clientX == null) return null;

        const relativeX = clientX - rect.left;
        const xScale = chart?.scales?.x;
        if (!xScale || typeof xScale.getValueForPixel !== "function") {
          return null;
        }

        const value = xScale.getValueForPixel(relativeX);
        const ms = Number(value);
        return Number.isFinite(ms) ? ms : null;
      } catch (_e) {
        return null;
      }
    },
    [chartRefs],
  );

  const handlePeriodWheel = useCallback(
    (e: WheelEvent, chartIdx: number) => {
      if (isRealtimeRef.current) return;

      // 稀にxレンジが壊れる(例:0..1)ため、必要なら復旧してから処理する。
      repairBrokenXRangeIfNeeded(chartIdx);

      // Shift+ホイール: 時間移動（横パン）を滑らかに
      if (e.shiftKey) {
        const dy = e.deltaY;
        const dx = e.deltaX;
        const delta = Number.isFinite(dy) && dy !== 0 ? dy : dx;
        if (!Number.isFinite(delta) || delta === 0) return;

        e.preventDefault();
        e.stopPropagation();

        const range =
          getCurrentXRangeFromChart(chartIdx) ??
          (() => {
            const s = parseDateTimeLocal(appliedRangeRef.current.start);
            const e2 = parseDateTimeLocal(appliedRangeRef.current.end);
            if (!s || !e2) return null;
            return {
              min: Math.min(s.getTime(), e2.getTime()),
              max: Math.max(s.getTime(), e2.getTime()),
            };
          })();
        const spanMs = range
          ? Math.max(MIN_PERIOD_RANGE_MS, range.max - range.min)
          : 60_000;

        // 100ホイール単位で表示幅の ~1/8 動くくらい（体感優先、必要なら後で調整）
        const impulseMs = (delta / 100) * (spanMs / 8);
        addPanImpulse(chartIdx, impulseMs);
        return;
      }

      const updatePluginWheelSpeed = (multiplier: number) => {
        try {
          const range =
            getCurrentXRangeFromChart(chartIdx) ??
            (() => {
              const s =
                parseDateTimeLocal(appliedRangeRef.current.start) ??
                parseDateTimeLocal(startInputRef.current);
              const e2 =
                parseDateTimeLocal(appliedRangeRef.current.end) ??
                parseDateTimeLocal(endInputRef.current);
              if (!s || !e2) return null;
              const min = Math.min(s.getTime(), e2.getTime());
              const max = Math.max(s.getTime(), e2.getTime());
              return { min, max: Math.max(min + MIN_PERIOD_RANGE_MS, max) };
            })();

          const spanMs = range
            ? Math.max(MIN_PERIOD_RANGE_MS, range.max - range.min)
            : MIN_PERIOD_RANGE_MS;

          const baseSpeed = getAdaptiveWheelZoomSpeed(spanMs);
          const speed = clamp(
            baseSpeed * (Number.isFinite(multiplier) ? multiplier : 1),
            0.02,
            0.5,
          );

          const ref = chartRefs.current[chartIdx] as any;
          const chart = ref?.chart ?? ref;
          const wheel = chart?.options?.plugins?.zoom?.zoom?.wheel;
          if (wheel && typeof wheel === "object") {
            wheel.speed = speed;
          }
        } catch {
          // 無視
        }
      };

      const dy = e.deltaY;
      if (!Number.isFinite(dy) || dy === 0) return;

      // ズームインは plugin に任せる
      if (dy < 0) {
        // ズームインは体感を軽くするため、速度を少し上げる。
        updatePluginWheelSpeed(1.8);
        return;
      }

      const appliedStartAt =
        parseDateTimeLocal(appliedRangeRef.current.start) ??
        parseDateTimeLocal(startInputRef.current);
      const appliedEndAt =
        parseDateTimeLocal(appliedRangeRef.current.end) ??
        parseDateTimeLocal(endInputRef.current);
      if (!appliedStartAt || !appliedEndAt) return;
      const appliedSpanMsRaw =
        appliedEndAt.getTime() - appliedStartAt.getTime();
      if (!Number.isFinite(appliedSpanMsRaw)) return;
      const appliedSpanMs = Math.max(
        MIN_PERIOD_RANGE_MS,
        Math.abs(appliedSpanMsRaw),
      );

      // ズームアウト
      if (
        bpslogDataForDisplayRef.current.length === 0 ||
        chartPointsRef.current.length < 2
      ) {
        e.preventDefault();
        e.stopPropagation();

        const fallbackCenterMs = (() => {
          const center =
            (appliedStartAt.getTime() + appliedEndAt.getTime()) / 2;
          return Number.isFinite(center) ? center : Date.now();
        })();
        const centerMs = getCenterMsFromWheel(e, chartIdx) ?? fallbackCenterMs;

        const steps = Math.max(1, Math.min(10, Math.round(Math.abs(dy) / 100)));
        scheduleFetchPeriodRangeFromWheelZoomOut(
          centerMs,
          appliedSpanMs,
          steps,
        );
        return;
      }

      const ref = chartRefs.current[chartIdx] as any;
      const chart = ref?.chart ?? ref;
      const xScale = chart?.scales?.x;
      if (!xScale) return;

      const minRaw = xScale.min;
      const maxRaw = xScale.max;
      if (typeof minRaw !== "number" || typeof maxRaw !== "number") return;

      const appliedStartMs = appliedStartAt.getTime();
      const appliedEndMs = appliedEndAt.getTime();
      const epsilonMs = 1_000;
      const fullRange =
        minRaw <= Math.min(appliedStartMs, appliedEndMs) + epsilonMs &&
        maxRaw >= Math.max(appliedStartMs, appliedEndMs) - epsilonMs;
      if (!fullRange) {
        // フルレンジでないなら plugin に任せる
        updatePluginWheelSpeed(1);
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const fallbackCenterMs = (() => {
        const center = (appliedStartAt.getTime() + appliedEndAt.getTime()) / 2;
        return Number.isFinite(center) ? center : Date.now();
      })();

      const centerMs = getCenterMsFromWheel(e, chartIdx) ?? fallbackCenterMs;

      const steps = Math.max(1, Math.min(10, Math.round(Math.abs(dy) / 100)));
      scheduleFetchPeriodRangeFromWheelZoomOut(centerMs, appliedSpanMs, steps);
    },
    [
      appliedRangeRef,
      addPanImpulse,
      bpslogDataForDisplayRef,
      chartPointsRef,
      chartRefs,
      endInputRef,
      getCurrentXRangeFromChart,
      getCenterMsFromWheel,
      isRealtimeRef,
      repairBrokenXRangeIfNeeded,
      scheduleFetchPeriodRangeFromWheelZoomOut,
      startInputRef,
    ],
  );

  const handleRealtimeWheel = useCallback(
    (e: WheelEvent, chartIdx: number) => {
      if (!isRealtimeRef.current) return;
      e.preventDefault();
      const dy = e.deltaY;
      if (!Number.isFinite(dy) || dy === 0) return;

      const fallbackCenterMs = (() => {
        const selected = selectedTimestampForHeaderRef.current;
        if (!selected) return Date.now();
        const ms = new Date(selected).getTime();
        return Number.isFinite(ms) ? ms : Date.now();
      })();
      const centerMs = getCenterMsFromWheel(e, chartIdx) ?? fallbackCenterMs;

      // リアルタイムでズーム操作したら期間指定へ切り替える
      const factor = dy < 0 ? 0.5 : 2;
      switchToPeriodByZoom(factor, centerMs);
    },
    [
      getCenterMsFromWheel,
      isRealtimeRef,
      selectedTimestampForHeaderRef,
      switchToPeriodByZoom,
    ],
  );

  // pinch操作でrealtimeからperiodへ切り替える。
  useEffect(() => {
    if (!isRealtime) return;
    const el = leftStackRef.current;
    if (!el) return;

    const anyWindow = window as any;
    const Hammer = anyWindow?.Hammer;
    if (!Hammer) return;

    const manager = new Hammer.Manager(el);
    const pinch = new Hammer.Pinch();
    manager.add([pinch]);
    manager.get("pinch").set({ enable: true });

    let lastScale = 1;
    manager.on("pinchstart", (ev: any) => {
      lastScale = typeof ev?.scale === "number" ? ev.scale : 1;
    });
    manager.on("pinchend", (ev: any) => {
      const scale = typeof ev?.scale === "number" ? ev.scale : 1;
      const ratio = scale / (lastScale || 1);
      const centerMs = (() => {
        const selected = selectedTimestampForHeaderRef.current;
        if (!selected) return Date.now();
        const ms = new Date(selected).getTime();
        return Number.isFinite(ms) ? ms : Date.now();
      })();

      // リアルタイムでピンチしたら期間指定へ切り替える
      if (ratio > 1.08) switchToPeriodByZoom(0.5, centerMs);
      if (ratio < 0.92) switchToPeriodByZoom(2, centerMs);
    });

    return () => {
      try {
        manager.destroy();
      } catch (e) {
        // 無視
      }
    };
    // NOTE: 以前のコンポーネント内effectと依存配列を同一にして挙動差を出さない。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRealtime]);

  return {
    handleZoomOrPanComplete,
    handlePeriodWheel,
    handleRealtimeWheel,
  };
};
